import { of } from 'rxjs/observable/of';
import { ajax } from 'rxjs/observable/dom/ajax';
import { map } from 'rxjs/operators/map';
import { share } from 'rxjs/operators/share';

class AuthService {
  clientId = process.env.REACT_APP_CONDUCTOR_CLIENT_ID || '';
  clientSecret = process.env.REACT_APP_CONDUCTOR_SECRET || '';
  basePath = process.env.REACT_APP_AUTH_SERVER || '';
  networkAssetURL = process.env.REACT_APP_CONDUCTOR_ASSETS_URL || '';
  tokenPath = '/oauth/token';
  tokenRevokePath = '/oauth2/revoke';
  accountPath = '/networkAsset/airfinder/accounts';
  airfinderPath = '/networkAsset/airfinder';
  
  accessURL = process.env.REACT_APP_ACCESS_SERVER;
  
  /**
    Save the refresh token in local storage
    @param {Object} data Response data from authentication
    @returns {Object} the same data
  */
  saveToken = data => {
    let token = Object.assign({last_refresh: Date.now()}, data.response);
    token.expires_in = token.expires_in * 1000 //was in seconds need ms for timestamp in js
    localStorage.setItem('conductor_auth_token', JSON.stringify(token));
    return data;
  };

  /**
    Get the refresh token from local storage
    @returns {String} the refresh token
  */
  getToken = () => {
    let t = {}
    try{
      let raw = localStorage.getItem('conductor_auth_token');
      if(!raw){
        return;
      }
      t = JSON.parse(raw);
    }catch(e){
      t = {}
    }
    return t;
  };
  /**
    Check if the token is valid
    @returns {Boolean}
  */
  tokenValid = ()=>{
    let t = this.getToken();
    if(!t.access_token){
      return false;
    }
    if(!t.refresh_token && !t.last_refresh){
      return true; //This probably is not going to end well
    }
    return (t.last_refresh  + t.expires_in) > Date.now();
  }

  /**
    Check if the token has expired
    @returns {Boolean}
  */
  isTokenExpired = () => {
    let t = this.getToken();
    const currentTime = Date.now();
    const expireTime = t.last_refresh + t.expires_in
    return expireTime <= currentTime;
  };

  /**
    Authenticate a user with password
    @returns {Observable} Emits the user data
  */
  authenticate = (username, password) => {
    return ajax({
      method: 'POST',
      url: `${this.basePath}${this.tokenPath}`,
      body:
        `grant_type=password&username=${encodeURIComponent(username)}` +
        `&password=${encodeURIComponent(password)}&` +
        `client_id=${this.clientId}&client_secret=${this.clientSecret}`
    }).pipe(map(data => this.saveToken(data)));
  };

  /**
    Authenticate a user with refresh token
    @returns {Observable} Emits the user data
  */
  authenticateFromToken = () => {
    this.refresh_token = this.getToken().refresh_token;
    return ajax({
      method: 'POST',
      url: `${this.basePath}${this.tokenPath}`,
      body:
        `grant_type=refresh_token&refresh_token=${this.refresh_token}&` +
        `client_id=${this.clientId}&client_secret=${this.clientSecret}`
    }).pipe(map(data => this.saveToken(data)));
  };

  /**
    Authenticate a user with refresh token with shared observable, concurrent calls to this function
    will use the same observable
    @returns {Observable} Emits the user data
  */
  authenticateFromTokenShared = () => {
    if (!this.newToken$ || this.getToken().refresh_token !== this.refresh_token) {
      this.newToken$ = this.authenticateFromToken().pipe(share());
    }
    return this.newToken$;
  }

  /**
    Create an account
    @param {String} email Account email for creation
    @param {String} recaptcha Recaptcha token
    @returns {Observable} Observable that emits the normalized response
  */
  createAccount = (email, recaptcha) => {
    return ajax({
      method: 'POST',
      url: `${this.networkAssetURL}${this.accountPath}`,
      body: { email, recaptcha },
      headers: { 'Content-Type': 'application/json' },
      responseType: 'json'
    });
  };

  forgotPassword = (email) => {
    let params = 'email='.concat(encodeURIComponent(email));
    let path = `${this.airfinderPath}/users/forgotPassword?${params}`;
    let url = `${this.networkAssetURL}${path}`;
    return ajax({
      method: 'GET',
      url: url,
      headers: { 'Content-Type': 'application/json' },
      responseType: 'json'
    });
  };

  resendVerify = (email) => {
    let params = 'email='.concat(encodeURIComponent(email));
    let path = `${this.airfinderPath}/users/verify/resend?${params}`;
    let url = `${this.networkAssetURL}${path}`;
    return ajax({
      method: 'GET',
      url: url,
      headers: { 'Content-Type': 'application/json' },
      responseType: 'json'
    });
  };
  
  verifyPasswordToken = (email, token)=>{
    let params = 'email='.concat(encodeURIComponent(email), '&key=',token);
    let path = `/access/users/verify/valid?${params}`;
    let url = `${this.accessURL}${path}`;
    return ajax({
      method: 'GET',
      url: url,
      headers: { 'Content-Type': 'application/json' },
      responseType: 'json'
    });
  }
  
  setPasswordWithToken = (email, token, password)=>{
    // POST /access/users/verify
    let body = {
      email: email,
      verificationKey: token,
      newPassword: password
    }
    let path = '/access/users/verify';
    let url = `${this.accessURL}${path}`;
    return ajax({
      method: 'POST',
      url: url,
      body: body,
      headers: { 'Content-Type': 'application/json' },
      responseType: 'json'
    });
  }

  /**
    Remove refresh token from local storage
    @returns {Observable} Observable that emits no error
  */
  revoke(){
    let sso = localStorage.getItem('sso');
    if(sso){
      try{
        let h = `${document.location.protocol}//${document.location.host}`;
        let email = localStorage.getItem('email');
        //these few lines save us updating/creating like 5 files.. yea redux+rxjs... yea
        this.logoutSSO(email, h).then(url=>{
          if(url){
            localStorage.clear();
            document.location.href = url;
          }
        })
      }catch(e){
        console.error('unable to process sso to logout', e)
      }
    }
    this.revokeToken().catch(() => {
      console.error('unable to call revoke, ignoring')
    });
    localStorage.clear();
    return of({
      error: false
    });
  };

  revokeToken() {
    const access_token = this.getToken().access_token;
    const url = `${this.basePath}${this.tokenRevokePath}`;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain', Authorization: `Bearer ${access_token}` },
      responseType: 'text'
    });
  }

  //use async as the js gods intended
  async logoutSSO(email,host){
    let params = 'email='.concat(encodeURIComponent(email),'&relayState=', host);
    let url = `${this.accessURL}/access/sso/logout?${params}`;
    let access_token = this.getToken().access_token;
    let r = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'text/plain', Authorization: `Bearer ${access_token}`},
      responseType: 'text'
    });
    let rd = await r.text();
    rd = rd.replace('redirect:', '');
    return rd;
  }

  checkSSO = (email, host) => {
    let params = 'email='.concat(encodeURIComponent(email),'&relayState=', host);
    let url = `${this.accessURL}/access/sso/login?${params}`;
    return ajax({
      url: url,
      method: 'GET',
      headers: { 'Content-Type': 'text/plain' },
      responseType: 'text'
    })
  };
}

export default new AuthService();



// WEBPACK FOOTER //
// ./src/services/auth/index.js